// src/pages/itsm/ITSMSettings.tsx
import { useState, useEffect } from 'react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Sun, Moon, Timer, Megaphone, Settings as SettingsIcon } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Btn, Pill, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'



export default function ITSMSettings() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [slaPolices, setSLAPolicies] = useState<any[]>([])
    const [categories, setCategories] = useState<any[]>([])
    const [announcements, setAnnouncements] = useState<any[]>([])
    const [activeTab, setActiveTab] = useState<'sla' | 'announcements' | 'general'>('sla')
    const [loading, setLoading] = useState(true)

    const [slaForm, setSlaForm] = useState({ name: '', priority: 'medium', response_hours: 4, resolution_hours: 24 })
    const [announcementForm, setAnnouncementForm] = useState({ title: '', body: '', type: 'info', ends_at: '' })
    const [showSlaAdd, setShowSlaAdd] = useState(false)
    const [showAnnouncementAdd, setShowAnnouncementAdd] = useState(false)
    const [confirmDeleteSla, setConfirmDeleteSla] = useState<{ id: number; name: string } | null>(null)

    useEffect(() => { fetchAll() }, [])

    const fetchAll = async () => {
        const [{ data: sla }, { data: cats }, { data: ann }] = await Promise.all([
            supabase.from('itsm_sla_policies').select('*').order('response_hours'),
            supabase.from('itsm_service_categories').select('*').order('sort_order'),
            supabase.from('itsm_announcements').select('*').order('created_at', { ascending: false }),
        ])
        setSLAPolicies(sla || [])
        setCategories(cats || [])
        setAnnouncements(ann || [])
        setLoading(false)
    }

    const saveSLA = async () => {
        await supabase.from('itsm_sla_policies').insert([slaForm])
        setSlaForm({ name: '', priority: 'medium', response_hours: 4, resolution_hours: 24 })
        setShowSlaAdd(false)
        fetchAll()
        showToast('SLA policy added', 'success')
    }

    const deleteSLA = async (id: number) => {
        await supabase.from('itsm_sla_policies').delete().eq('id', id)
        fetchAll()
        showToast('SLA policy deleted', 'success')
    }

    const saveAnnouncement = async () => {
        await supabase.from('itsm_announcements').insert([{
            ...announcementForm,
            ends_at: announcementForm.ends_at || null,
            created_by: 'Admin',
        }])
        setAnnouncementForm({ title: '', body: '', type: 'info', ends_at: '' })
        setShowAnnouncementAdd(false)
        fetchAll()
        showToast('Announcement published', 'success')
    }

    const toggleAnnouncement = async (id: number, is_active: boolean) => {
        await supabase.from('itsm_announcements').update({ is_active: !is_active }).eq('id', id)
        fetchAll()
        showToast(is_active ? 'Announcement deactivated' : 'Announcement activated', 'success')
    }

    const PRIORITY_TONE: Record<string, 'info' | 'warning' | 'danger' | 'violet'> = {
        low: 'info', medium: 'warning', high: 'danger', urgent: 'violet'
    }

    const TYPE_CONFIG: Record<string, { label: string; tone: 'info' | 'warning' | 'violet' | 'danger' }> = {
        info: { label: 'Info', tone: 'info' },
        warning: { label: 'Warning', tone: 'warning' },
        maintenance: { label: 'Maintenance', tone: 'violet' },
        outage: { label: 'Outage', tone: 'danger' },
    }

    const s = styles

    if (loading) return <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>ITSM Settings</h1>
                    <p style={s.subtitle}>Configure SLA policies, announcements, and system settings</p>
                </div>
                <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: T.surface2, padding: 4, borderRadius: 10, width: 'fit-content' }}>
                {[
                    { key: 'sla', label: 'SLA Policies', Icon: Timer },
                    { key: 'announcements', label: 'Announcements', Icon: Megaphone },
                    { key: 'general', label: 'General', Icon: SettingsIcon },
                ].map(tab => (
                    <button key={tab.key}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            background: activeTab === tab.key ? T.surface : 'transparent', color: activeTab === tab.key ? T.text : T.text2,
                            boxShadow: activeTab === tab.key ? T.shadowSm : 'none',
                        }}
                        onClick={() => setActiveTab(tab.key as any)}>
                        <tab.Icon size={13} />{tab.label}
                    </button>
                ))}
            </div>

            {/* SLA Policies */}
            {activeTab === 'sla' && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <p style={s.cardTitle}>SLA Policies</p>
                        <Btn variant="primary" style={undefined} onClick={() => setShowSlaAdd(true)}>+ Add Policy</Btn>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `0.5px solid ${T.border}` }}>
                                    {['Name', 'Priority', 'First Response', 'Resolution', ''].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {slaPolices.map(sla => (
                                    <tr key={sla.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                                        <td style={s.td}><span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{sla.name}</span></td>
                                        <td style={s.td}>
                                            <Pill tone={PRIORITY_TONE[sla.priority] || 'info'}>{sla.priority?.toUpperCase()}</Pill>
                                        </td>
                                        <td style={s.td}><span style={{ fontSize: 13, color: T.text2 }}>{sla.response_hours}h</span></td>
                                        <td style={s.td}><span style={{ fontSize: 13, color: T.text2 }}>{sla.resolution_hours}h</span></td>
                                        <td style={s.td}>
                                            <Btn size="sm" style={{ background: T.dangerBg, color: T.danger, borderColor: T.dangerBorder }}
                                                onClick={() => setConfirmDeleteSla({ id: sla.id, name: sla.name })}>Delete</Btn>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {showSlaAdd && (
                        <div style={{ marginTop: 20, padding: 16, background: T.surface2, borderRadius: 10, border: `0.5px solid ${T.border}` }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>New SLA Policy</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                                <div style={s.field}>
                                    <label style={s.label}>Name *</label>
                                    <input style={s.input} value={slaForm.name} onChange={e => setSlaForm({ ...slaForm, name: e.target.value })} placeholder="High Priority SLA" />
                                </div>
                                <div style={s.field}>
                                    <label style={s.label}>Priority</label>
                                    <select style={s.input} value={slaForm.priority} onChange={e => setSlaForm({ ...slaForm, priority: e.target.value })}>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                                <div style={s.field}>
                                    <label style={s.label}>Response (hours)</label>
                                    <input type="number" style={s.input} value={slaForm.response_hours} onChange={e => setSlaForm({ ...slaForm, response_hours: Number(e.target.value) })} />
                                </div>
                                <div style={s.field}>
                                    <label style={s.label}>Resolution (hours)</label>
                                    <input type="number" style={s.input} value={slaForm.resolution_hours} onChange={e => setSlaForm({ ...slaForm, resolution_hours: Number(e.target.value) })} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                                <Btn style={s.btnOutline} onClick={() => setShowSlaAdd(false)}>Cancel</Btn>
                                <Btn variant="primary" style={undefined} onClick={saveSLA}>Save</Btn>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* Announcements */}
            {activeTab === 'announcements' && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <p style={s.cardTitle}>System Announcements</p>
                        <Btn variant="primary" style={undefined} onClick={() => setShowAnnouncementAdd(true)}>+ New Announcement</Btn>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {announcements.map(ann => {
                            const type = TYPE_CONFIG[ann.type] || TYPE_CONFIG.info
                            return (
                                <div key={ann.id} style={{ padding: 16, background: T[(type.tone + 'Bg') as keyof typeof T] as string, borderRadius: 10, border: `0.5px solid ${T[(type.tone + 'Border') as keyof typeof T]}`, opacity: ann.is_active ? 1 : 0.5 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Pill tone={type.tone}>{type.label.toUpperCase()}</Pill>
                                                {!ann.is_active && <span style={{ fontSize: 11, color: T.text3, fontWeight: 600 }}>INACTIVE</span>}
                                            </div>
                                            <p style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>{ann.title}</p>
                                            <p style={{ fontSize: 13, color: T.text2 }}>{ann.body}</p>
                                            {ann.ends_at && <p style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>Expires: {new Date(ann.ends_at).toLocaleDateString('en-GB')}</p>}
                                        </div>
                                        <Btn size="sm" style={{ marginLeft: 12, ...(ann.is_active ? { background: T.dangerBg, color: T.danger, borderColor: T.dangerBorder } : { background: T.successBg, color: T.success, borderColor: T.successBorder }) }}
                                            onClick={() => toggleAnnouncement(ann.id, ann.is_active)}>
                                            {ann.is_active ? 'Deactivate' : 'Activate'}
                                        </Btn>
                                    </div>
                                </div>
                            )
                        })}
                        {announcements.length === 0 && <p style={{ textAlign: 'center', color: T.text3, padding: '20px', fontSize: 13 }}>No announcements yet</p>}
                    </div>

                    {showAnnouncementAdd && (
                        <div style={s.overlay}>
                            <div style={s.modal}>
                                <h2 style={s.modalTitle}>New Announcement</h2>
                                <div style={s.field}>
                                    <label style={s.label}>Title *</label>
                                    <input style={s.input} value={announcementForm.title} onChange={e => setAnnouncementForm({ ...announcementForm, title: e.target.value })} placeholder="Scheduled Maintenance" />
                                </div>
                                <div style={s.field}>
                                    <label style={s.label}>Message *</label>
                                    <textarea style={{ ...s.input, height: 80, padding: '8px 10px', resize: 'none' as const }} value={announcementForm.body} onChange={e => setAnnouncementForm({ ...announcementForm, body: e.target.value })} placeholder="System will be down for maintenance..." />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div style={s.field}>
                                        <label style={s.label}>Type</label>
                                        <select style={s.input} value={announcementForm.type} onChange={e => setAnnouncementForm({ ...announcementForm, type: e.target.value })}>
                                            <option value="info">Info</option>
                                            <option value="warning">Warning</option>
                                            <option value="maintenance">Maintenance</option>
                                            <option value="outage">Outage</option>
                                        </select>
                                    </div>
                                    <div style={s.field}>
                                        <label style={s.label}>Expires On</label>
                                        <input type="date" style={s.input} value={announcementForm.ends_at} onChange={e => setAnnouncementForm({ ...announcementForm, ends_at: e.target.value })} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <Btn style={s.btnOutline} onClick={() => setShowAnnouncementAdd(false)}>Cancel</Btn>
                                    <Btn variant="primary" style={undefined} onClick={saveAnnouncement}>Publish</Btn>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* General */}
            {activeTab === 'general' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Service Categories Overview */}
                    <Card>
                        <p style={s.cardTitle}>Service Categories</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
                            {categories.map(cat => (
                                <div key={cat.id} style={{ padding: '12px 14px', background: cat.color + '10', borderRadius: 8, border: `0.5px solid ${cat.color}30`, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 20 }}>{cat.icon}</span>
                                    <div>
                                        <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{cat.name}</p>
                                        <p style={{ fontSize: 11, color: T.text3 }}>{cat.is_active ? 'Active' : 'Inactive'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* System Info */}
                    <Card>
                        <p style={s.cardTitle}>System Information</p>
                        <table style={{ width: '100%', marginTop: 12 }}>
                            <tbody>
                                {[
                                    { label: 'Version', value: 'RITIS ITSM v1.0' },
                                    { label: 'Database', value: 'Supabase PostgreSQL' },
                                    { label: 'Environment', value: 'Production' },
                                    { label: 'URL', value: 'ritisbase.com' },
                                ].map(r => (
                                    <tr key={r.label} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                                        <td style={{ fontSize: 12, color: T.text3, padding: '8px 0', paddingRight: 16, fontWeight: 600 }}>{r.label}</td>
                                        <td style={{ fontSize: 13, color: T.text, fontWeight: 500, padding: '8px 0' }}>{r.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            )}

            <ConfirmDialog
                open={!!confirmDeleteSla}
                onClose={() => setConfirmDeleteSla(null)}
                onConfirm={() => confirmDeleteSla && deleteSLA(confirmDeleteSla.id)}
                title="Delete SLA policy?"
                message={`This will permanently delete "${confirmDeleteSla?.name}". This cannot be undone.`}
                confirmLabel="Delete"
                danger
            />
            <ToastHost />
        </div>
    )
}

function ThemeToggle({ theme, toggleTheme }: { theme: 'light' | 'dark'; toggleTheme: () => void }) {
    return (
        <button onClick={toggleTheme} aria-label="Toggle theme" style={{
            width: 34, height: 34, borderRadius: T.radiusSm, background: T.surface,
            border: `1px solid ${T.borderStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: T.text2, flexShrink: 0,
        }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1000, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    cardTitle: { fontSize: 13, fontWeight: 700, color: T.text },
    btnOutline: { flex: 1, background: 'transparent' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: 480, border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `0.5px solid ${T.border}` },
}
